import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useQueryClient } from "react-query";
import moment from "moment";
import * as openpgp from "openpgp";
import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { FileUploader } from "react-drag-drop-files";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileContract,
  faGripVertical,
} from "@fortawesome/free-solid-svg-icons";
import ContractCruMetricModal from "components/ContractCruMetricModal";
import { IContractCruMetric, emptyCruMetric } from "dto/Contracts/IContractCru";

import ModalLayout, { DockPosition } from "components/ModalLayout";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Input from "components/Input";
import DatePickerNew from "components/DatePickerNew";
import SearchEntities from "components/SearchEntities";
import CloseIcon from "assets/icons/close.svg?react";

import ModalsManagerContext from "contexts/ModalsManager";
import parcelService from "api/parcelService";
import useFiles, { FileExt } from "hooks/useFiles";
import usePDF from "hooks/usePDF";
import ShowPreviewModal from "components/ShowPreviewModal";
import ScanDocumentModal from "components/ScanDocumentModal";
import { IEntity } from "dto/IEntity";
import {
  EParcelAccess,
  EParcelAttachmentKind,
  IParcelLookup,
} from "dto/Mailroom/IMailroom";
import {
  ISingleBinaryKeys,
  generateSingleBinary,
} from "utils/GenerateSingleBinary";
import { getKeys } from "utils/getKeys";
import { getUserId } from "utils/getUserId";
import { readFileFromBase64 } from "utils/readFileFromBase64";
import { handleCreate } from "utils/uploadFile";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";
import { IAttachmentMeta, WizardStep, emptyAttachmentMeta } from "./typings";

/** Istniejący załącznik w trybie edycji — placeholder File z Uri (bez bajtów; plik jest zaszyfrowany na serwerze). */
interface ExistingFileExt extends FileExt {
  existing?: boolean;
}

interface Props {
  /**
   * Tryb edycji zarejestrowanej przesyłki (port otwierania kreatora Magistratu na istniejącej
   * przesyłce): kreator ładuje dane z GET incomingParcel/{id}/edit, pokazuje istniejące
   * załączniki jako pozycje bez podglądu, pozwala je usuwać / przestawiać / opisywać i dokładać
   * nowe skany (szyfrowane kluczem publicznym przesyłki), a zapis idzie PUT incomingParcel.
   * Sygnatura, skład i dekretujący są tylko do odczytu.
   */
  editParcelId?: string;
}

const IncomingParcelWizard = ({ editParcelId }: Props) => {
  const auth = useAuth();
  const userId = getUserId(auth.user!);
  const { modals, setModals } = useContext(ModalsManagerContext);
  const queryClient = useQueryClient();
  const refWrapper = useRef(null);
  // Snapshot na czas życia modala: `modalOptions` są współdzielone (np. SettingsModal robi
  // setOptions(undefined) przy otwarciu) — bez tego kreator przełączyłby się w trakcie edycji
  // w tryb tworzenia i zapisał placeholdery załączników jako nową przesyłkę.
  const [editId] = useState(editParcelId);
  const isEdit = !!editId;

  const workspaceId =
    localStorage.getItem("workspaceId") ??
    sessionStorage.getItem("workspaceId") ??
    undefined;

  const { data: initData } = parcelService.useInitParcel(
    workspaceId ?? undefined
  );
  const init = initData?.data;

  const { mutate: createParcel } = parcelService.useCreateParcel();
  const { mutate: updateParcel } = parcelService.useUpdateIncomingParcel();
  const { data: editData, isError: editError } =
    parcelService.useGetIncomingParcelForEdit(editId);
  const edit = editData?.data;
  const { files, setFiles, addNewFile, removeFile } = useFiles();
  const { blob, addBlob, removeBlob } = usePDF();

  const [step, setStep] = useState<WizardStep>(WizardStep.Attachments);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");

  // Metadane załączników — równolegle do listy plików (indeks == kolejność).
  const [attMeta, setAttMeta] = useState<IAttachmentMeta[]>([]);

  // Stan przesyłki
  const [isDamaged, setIsDamaged] = useState(false);
  const [damageDescription, setDamageDescription] = useState("");
  const [passedOnWithoutOpening, setPassedOnWithoutOpening] = useState(false);

  // Nadawca + główne pola
  const [sender, setSender] = useState<IEntity | null>(null);
  const [receiveDate, setReceiveDate] = useState<Date | undefined>(new Date());
  const [deliveryKindId, setDeliveryKindId] = useState<number | "">("");
  const [carrierSignature, setCarrierSignature] = useState("");

  // Opis przesyłki
  const [registryOfficeId, setRegistryOfficeId] = useState<string | "">("");
  const [parcelStoreId, setParcelStoreId] = useState<string | "">("");
  const [access, setAccess] = useState<EParcelAccess>(EParcelAccess.Public);
  const [receiverId, setReceiverId] = useState<string | "">("");

  const [, setActualFile] = useState(0);
  const [, setActualProgress] = useState<number | null>(null);
  const [showScan, setShowScan] = useState(false);
  const [editLoaded, setEditLoaded] = useState(false);

  // Tryb edycji: jednorazowe zasilenie stanu kreatora danymi przesyłki. Istniejące załączniki
  // wchodzą na listę plików jako placeholdery (existing=true, uri) — dzięki temu kolejność,
  // metadane i usuwanie działają tym samym kodem co dla nowych plików.
  useEffect(() => {
    if (!isEdit || !edit || editLoaded) return;
    const sorted = [...edit.Attachments].sort((a, b) => a.Order - b.Order);
    setFiles(
      sorted.map((a, i) => {
        const f = new File(
          [],
          a.FileName || a.Title || `Załącznik ${i + 1}`
        ) as ExistingFileExt;
        f.existing = true;
        f.uri = a.Uri;
        return f;
      })
    );
    setAttMeta(
      sorted.map((a) => ({
        typeId: a.TypeId ?? null,
        title: a.Title ?? "",
        signature: a.Signature ?? "",
        isDigital: a.IsDigital,
        digitalMapping: a.DigitalMapping,
      }))
    );
    setIsDamaged(edit.IsDamaged);
    setDamageDescription(edit.DamageDescription ?? "");
    setPassedOnWithoutOpening(edit.PassedOnWithoutOpening);
    setSender(
      edit.SenderId != null
        ? ({
            Id: edit.SenderId,
            Name: edit.SenderName ?? "",
          } as unknown as IEntity)
        : null
    );
    setReceiveDate(edit.ReceiveDate ? new Date(edit.ReceiveDate) : undefined);
    setDeliveryKindId(edit.DeliveryKindId ?? "");
    setCarrierSignature(edit.CarrierSignature ?? "");
    setRegistryOfficeId(edit.RegistryOfficeId ?? "");
    setParcelStoreId(edit.ParcelStoreId ?? "");
    setAccess(edit.Access);
    setReceiverId(edit.ReceiverId ?? "");
    setEditLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, edit, editLoaded]);

  useEffect(() => {
    if (!editError) return;
    notification({
      type: ENotificationType.ERROR,
      title: "Nie można edytować przesyłki",
      text: "Brak dostępu albo przesyłka nie istnieje.",
    });
    setModals({ ...modals, editIncomingParcel: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editError]);

  // Główny załącznik rodzaju „Umowa" → wpis w rejestrze umów z metryką CRU
  // (port dekretacji z Magistratu; walidacja odroczona — komplet w rejestrze).
  const [cruMetric, setCruMetric] = useState<IContractCruMetric | null>(null);
  const [showCruMetric, setShowCruMetric] = useState(false);
  const mainIsContract = useMemo(() => {
    const id = attMeta[0]?.typeId;
    return (
      init?.AttachmentTypes?.find((t) => t.Id === id)?.Kind ===
      EParcelAttachmentKind.Contract
    );
  }, [attMeta, init]);

  // Synchronizacja metadanych z listą plików.
  useEffect(() => {
    setAttMeta((prev) => {
      if (prev.length === files.length) return prev;
      const next = [...prev];
      while (next.length < files.length) next.push(emptyAttachmentMeta());
      next.length = files.length;
      return next;
    });
  }, [files.length]);

  // Domyślne ustawienia z /init (biuro podawcze, skład, dekretujący). W edycji wartości
  // pochodzą z przesyłki — nie nadpisujemy ich domyślnymi.
  useEffect(() => {
    if (!init || isEdit) return;

    if (receiverId === "") {
      const def =
        init.DefaultReceiverId ?? init.Receivers?.find((r) => r.IsMe)?.Id ?? "";
      setReceiverId(def ?? "");
    }
    if (registryOfficeId === "") {
      const def =
        init.DefaultRegistryOfficeId ??
        init.RegistryOffices?.find((o) => o.IsDefault)?.Id ??
        "";
      setRegistryOfficeId(def ?? "");
    }
    if (parcelStoreId === "") {
      const def =
        init.DefaultParcelStoreId ??
        init.ParcelStores?.find((s) => s.IsDefault)?.Id ??
        "";
      setParcelStoreId(def ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init]);

  const close = () =>
    setModals(
      isEdit
        ? { ...modals, editIncomingParcel: false }
        : { ...modals, createPostage: false }
    );

  const updateMeta = (index: number, patch: Partial<IAttachmentMeta>) =>
    setAttMeta((prev) =>
      prev.map((m, i) => (i === index ? { ...m, ...patch } : m))
    );

  // Zmiana kolejności załączników (drag&drop) — reorder plików i metadanych razem.
  // Pierwszy (index 0) jest głównym i wyznacza kategorię przesyłki.
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;

    setFiles((prev) => {
      const next = Array.from(prev);
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setAttMeta((prev) => {
      const next = Array.from(prev);
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const handleShowPreview = (file: File) => {
    // Istniejący załącznik (edycja) — placeholder bez bajtów; podgląd z widoku przesyłki.
    if ((file as ExistingFileExt).existing) return;
    const newBlob = new Blob([file], { type: file.type });
    addBlob(newBlob);
  };

  // ── Nawigacja / kolejność kroków ──────────────────────────────────────────

  // Ścieżka bez otwierania pomija opis głównego załącznika.
  const steps: WizardStep[] = passedOnWithoutOpening
    ? [WizardStep.Attachments, WizardStep.ParcelState, WizardStep.Description]
    : [
        WizardStep.Attachments,
        WizardStep.ParcelState,
        WizardStep.MainAttachment,
        WizardStep.Description,
      ];

  const stepIndex = Math.max(0, steps.indexOf(step));
  const isLast = step === WizardStep.Description;

  const validateStep = (): string | null => {
    switch (step) {
      case WizardStep.Attachments:
        if (files.length === 0) return "Dodaj przynajmniej jeden plik/skan.";
        return null;
      case WizardStep.ParcelState:
        if (isDamaged && !damageDescription.trim())
          return "Podaj opis uszkodzenia.";
        return null;
      case WizardStep.MainAttachment:
        if (!sender) return "Wskaż nadawcę.";
        if (!attMeta[0]?.typeId) return "Wskaż rodzaj głównego załącznika.";
        if (!deliveryKindId) return "Wskaż formę doręczenia.";
        if (!receiveDate) return "Wskaż datę wpływu.";
        if (!attMeta[0]?.title?.trim())
          return "Podaj tytuł głównego załącznika.";
        return null;
      case WizardStep.Description:
        return null;
      default:
        return null;
    }
  };

  const goNext = () => {
    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    const next = steps[Math.min(stepIndex + 1, steps.length - 1)];
    setStep(next);
  };

  const goBack = () => {
    setError("");
    setStep(steps[Math.max(stepIndex - 1, 0)]);
  };

  // Kategoria przesyłki = rodzaj głównego (index 0) załącznika (read-only podgląd).
  const mainTypeName = useMemo(() => {
    const id = attMeta[0]?.typeId;
    return init?.AttachmentTypes?.find((t) => t.Id === id)?.Name ?? "—";
  }, [attMeta, init]);

  // ── Zapis ────────────────────────────────────────────────────────────────

  const resolveKeys = async (
    receiverPublicKey: string | null
  ): Promise<ISingleBinaryKeys> => {
    if (receiverPublicKey) {
      const pk = await readFileFromBase64(
        receiverPublicKey,
        "application/other"
      );
      const armored = new TextDecoder().decode(pk);
      const key = await openpgp.key.readArmored(armored);
      return generateSingleBinary(null, null, key);
    }
    const currentUserKeys = await getKeys(userId!);
    return generateSingleBinary(
      undefined,
      currentUserKeys?.intermidatePrivateKey
    );
  };

  // Zapis edycji: nowe pliki szyfrujemy kluczem publicznym PRZESYŁKI (klucz obiektu z BE —
  // ten sam, którym zaszyfrowano dotychczasowe załączniki; odbiorca odszyfruje je jednym kluczem),
  // istniejące idą po Uri z nowymi metadanymi/kolejnością; pominięte BE usuwa z przesyłki.
  const submitEdit = async () => {
    if (!edit) return;
    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const newFiles = files.filter((f) => !(f as ExistingFileExt).existing);
      if (newFiles.length > 0) {
        if (!edit.PublicKey) {
          setIsLoading(false);
          setError(
            "Przesyłka nie ma klucza szyfrowania — nie można dołożyć plików."
          );
          return;
        }
        const pkBytes = await readFileFromBase64(
          edit.PublicKey,
          "application/other"
        );
        const parcelKey = await openpgp.key.read(new Uint8Array(pkBytes));

        for (const file of newFiles) {
          const r = await handleCreate(
            [file],
            undefined,
            parcelKey,
            true,
            true,
            undefined,
            undefined,
            (_fileIndex: number, progress: number) =>
              setActualProgress(progress),
            false
          );
          if (!r) {
            setIsLoading(false);
            return;
          }
          (file as FileExt).uri = r[0].uri;
          (file as FileExt).keyId = r[0].keyId;
        }
      }

      updateParcel(
        {
          Id: edit.Id,
          SenderId: sender ? Number(sender.Id) : null,
          ReceiveDate: receiveDate ? moment(receiveDate).toISOString() : null,
          Title: attMeta[0]?.title || null,
          DeliveryKindId: deliveryKindId === "" ? null : deliveryKindId,
          RegistryOfficeId: registryOfficeId === "" ? null : registryOfficeId,
          CarrierSignature: carrierSignature || null,
          Access: access,
          IsDamaged: isDamaged,
          DamageDescription: isDamaged ? damageDescription : null,
          PassedOnWithoutOpening: passedOnWithoutOpening,
          Attachments: files.map((file, index) => ({
            Uri: (file as FileExt).uri!,
            Order: index,
            TypeId: attMeta[index]?.typeId ?? null,
            Title: attMeta[index]?.title || null,
            Signature: attMeta[index]?.signature || null,
            IsDigital: attMeta[index]?.isDigital ?? false,
            DigitalMapping: attMeta[index]?.digitalMapping ?? false,
            Number: index + 1,
          })),
        },
        {
          onSuccess: () => {
            setIsLoading(false);
            notification({
              type: ENotificationType.SUCCESS,
              title: "Zapisano zmiany",
              text: edit.ParcelNumber
                ? `Przesyłka ${edit.ParcelNumber} została zaktualizowana.`
                : "Przesyłka została zaktualizowana.",
            });
            // Grid biura (MessagesRegister: "packages") + listy/wątek modułu przesyłek (listService.*).
            queryClient.invalidateQueries("packages");
            queryClient.invalidateQueries("listService.getPackage");
            queryClient.invalidateQueries("listService.getListOfMessages");
            queryClient.invalidateQueries("listService.getListOfPackages");
            queryClient.invalidateQueries("parcelService.incomingEdit");
            close();
          },
          onError: (e) => {
            setIsLoading(false);
            notification({
              type: ENotificationType.ERROR,
              title: "Błąd zapisu",
              text:
                (e as { response?: { data?: { Message?: string } } })?.response
                  ?.data?.Message ?? "Nie udało się zapisać zmian.",
            });
          },
        }
      );
    } catch {
      setIsLoading(false);
      notification({
        type: ENotificationType.ERROR,
        title: "Błąd zapisu",
        text: "Nie udało się zapisać zmian.",
      });
    }
  };

  const submit = async (pass: boolean) => {
    if (isEdit) {
      await submitEdit();
      return;
    }
    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Dekretujący: dla „Przekaż" przypisujemy przesyłkę wybranemu pracownikowi
      // (szyfrujemy jego kluczem). Dla „Zapisz" zostaje u rejestrującego (self).
      const chosen =
        pass && receiverId
          ? init?.Receivers?.find((r) => r.Id === receiverId)
          : undefined;
      const isSelf = !chosen || chosen.IsMe;

      const keys = await resolveKeys(isSelf ? null : chosen!.PublicKey);

      for (const file of files) {
        const r = await handleCreate(
          [file],
          undefined,
          keys.cleanPublicKey,
          true,
          true,
          undefined,
          undefined,
          (_fileIndex: number, progress: number) => setActualProgress(progress),
          false
        );

        if (!r) {
          setIsLoading(false);
          return;
        }

        (file as FileExt).uri = r[0].uri;
        (file as FileExt).keyId = r[0].keyId;
        setActualFile((p) => p + 1);
        setActualProgress(0);
      }

      const attachments = files.map((file, index) => ({
        Order: index,
        Uri: (file as FileExt).uri!,
        TypeId: attMeta[index]?.typeId ?? null,
        Title: attMeta[index]?.title || null,
        Signature: attMeta[index]?.signature || null,
        IsDigital: attMeta[index]?.isDigital ?? false,
        DigitalMapping: attMeta[index]?.digitalMapping ?? false,
        Number: index + 1,
      }));

      createParcel(
        {
          Attachments: attachments,
          CaseId: null,
          ReceiveDate: moment(receiveDate).toISOString(),
          ReceiverId: isSelf ? null : chosen!.Id,
          SenderId: sender ? sender.Id : null,
          Title: attMeta[0]?.title || null,
          DeliveryKindId: deliveryKindId === "" ? null : deliveryKindId,
          RegistryOfficeId: registryOfficeId === "" ? null : registryOfficeId,
          ParcelStoreId: parcelStoreId === "" ? null : parcelStoreId,
          CarrierSignature: carrierSignature || null,
          Access: access,
          IsDamaged: isDamaged,
          DamageDescription: isDamaged ? damageDescription : null,
          PassedOnWithoutOpening: passedOnWithoutOpening,
          PublicKey: keys.PublicKey,
          PrivateKey: keys.PrivateKey,
          ContractCruMetric: mainIsContract ? cruMetric : null,
        },
        {
          onSuccess: (res) => {
            setIsLoading(false);
            const signature = res?.data?.Items?.[0]?.ParcelNumber;
            const baseText = pass
              ? "Przesyłka została zarejestrowana i przekazana."
              : "Przesyłka została zarejestrowana.";
            notification({
              type: ENotificationType.SUCCESS,
              title: "Zarejestrowano przesyłkę",
              text: signature
                ? `${baseText} Sygnatura: ${signature}`
                : baseText,
            });
            close();
          },
          onError: () => {
            setIsLoading(false);
            notification({
              type: ENotificationType.ERROR,
              title: "Błąd rejestracji",
              text: "Nie udało się zarejestrować przesyłki.",
            });
          },
        }
      );
    } catch {
      setIsLoading(false);
      notification({
        type: ENotificationType.ERROR,
        title: "Błąd rejestracji",
        text: "Nie udało się zarejestrować przesyłki.",
      });
    }
  };

  // ── Renderowanie kroków ────────────────────────────────────────────────────

  const renderAttachments = () => (
    <div className={styles.row}>
      <div className={styles.headerRow}>
        <div className={styles.stepHeader}>Załączniki</div>
        <Button text="+ Skanuj" onClick={() => setShowScan(true)} />
      </div>

      {files.length === 0 ? (
        <FileUploader
          multiple
          handleChange={(f: FileList) =>
            setFiles([...files, ...(Array.from(f) as File[])])
          }
          name="file"
          children={
            <div className={styles.dropzone}>
              <div>Przeciągnij pliki tutaj</div>
              <div className={styles.dropzoneCta}>lub wybierz z dysku</div>
            </div>
          }
        />
      ) : (
        <>
          <div className={styles.hint}>
            Określ rodzaj każdego załącznika. Rodzaj głównego (pierwszego)
            wyznacza kategorię przesyłki — kolejność zmienisz przeciągając.
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="parcel-attachments">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={styles.row}
                >
                  {files.map((file, index) => (
                    <Draggable
                      key={index}
                      draggableId={`att-${index}`}
                      index={index}
                    >
                      {(drag) => (
                        <div
                          ref={drag.innerRef}
                          {...drag.draggableProps}
                          className={styles.attachmentCard}
                        >
                          <div className={styles.attachmentHead}>
                            <span
                              className={styles.dragHandle}
                              {...drag.dragHandleProps}
                            >
                              <FontAwesomeIcon icon={faGripVertical} />
                            </span>
                            <span
                              className={styles.fileName}
                              onClick={() => handleShowPreview(file)}
                              title={
                                (file as ExistingFileExt).existing
                                  ? "Załącznik zarejestrowany wcześniej"
                                  : "Podgląd"
                              }
                            >
                              {file.name}
                            </span>
                            {index === 0 && (
                              <span className={styles.mainBadge}>Główny</span>
                            )}
                            {(file as ExistingFileExt).existing && (
                              <span className={styles.existingBadge}>
                                zapisany
                              </span>
                            )}
                          </div>
                          {renderTypeSelect(index)}
                          <div className={styles.cardActions}>
                            <button
                              type="button"
                              className={styles.removeBtn}
                              onClick={() => removeFile(index)}
                            >
                              Usuń
                            </button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <FileUploader
            multiple
            handleChange={(f: FileList) =>
              setFiles([...files, ...(Array.from(f) as File[])])
            }
            name="file"
            children={<div className={styles.addBar}>+ Dodaj kolejny plik</div>}
          />
        </>
      )}
    </div>
  );

  const renderParcelState = () => (
    <div className={styles.row}>
      <div className={styles.stepHeader}>Stan przesyłki</div>
      <label className={styles.check}>
        <Checkbox
          checked={passedOnWithoutOpening}
          onChange={(e) => setPassedOnWithoutOpening(e.target.checked)}
        />
        Przekazana bez otwierania
      </label>
      <label className={styles.check}>
        <Checkbox
          checked={isDamaged}
          onChange={(e) => setIsDamaged(e.target.checked)}
        />
        Uszkodzona
      </label>
      {isDamaged && (
        <Input
          variant="outlined"
          label="Opis uszkodzenia"
          value={damageDescription}
          placeholder="Opis uszkodzenia"
          onChange={setDamageDescription}
        />
      )}
    </div>
  );

  // Słownik rodzajów załącznika jest hierarchiczny (jak lista pomocnicza Magistratu:
  // „Dokumenty księgowe" → Faktura / PEF / KSeF, „Inne" → UPP / UPD / …): pozycje
  // główne wg SortOrder, podpozycje wcięte bezpośrednio pod rodzicem.
  const attachmentTypeOptions = useMemo(() => {
    const all = init?.AttachmentTypes ?? [];
    const bySort = (a: IParcelLookup, b: IParcelLookup) =>
      a.SortOrder - b.SortOrder || a.Name.localeCompare(b.Name, "pl");
    const roots = all.filter((t) => !t.ParentId).sort(bySort);
    const out: { item: IParcelLookup; level: number }[] = [];
    for (const r of roots) {
      out.push({ item: r, level: 0 });
      all
        .filter((t) => t.ParentId === r.Id)
        .sort(bySort)
        .forEach((c) => out.push({ item: c, level: 1 }));
    }
    // Sieroty (rodzic usunięty) — na końcu, jako pozycje główne.
    all
      .filter((t) => t.ParentId && !roots.some((r) => r.Id === t.ParentId))
      .sort(bySort)
      .forEach((t) => out.push({ item: t, level: 0 }));
    return out;
  }, [init]);

  const renderTypeSelect = (index: number) => (
    <FormControl fullWidth size="small">
      <InputLabel>Rodzaj załącznika</InputLabel>
      <Select
        label="Rodzaj załącznika"
        value={attMeta[index]?.typeId ?? ""}
        onChange={(e) => {
          const v = e.target.value as string | number;
          updateMeta(index, { typeId: v === "" ? null : Number(v) });
        }}
      >
        {attachmentTypeOptions.map(({ item, level }) => (
          <MenuItem
            key={item.Id}
            value={item.Id}
            sx={{ pl: 2 + level * 2.5, fontWeight: level === 0 ? 500 : 400 }}
          >
            {item.Name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderMainAttachment = () => (
    <div className={styles.row}>
      <div className={styles.stepHeader}>Główny załącznik i nadawca</div>
      {isEdit && (
        <div className={styles.readonly}>
          Obecny nadawca:{" "}
          <strong>{sender?.Name || sender?.Label || "—"}</strong> (wyszukaj
          poniżej, aby zmienić)
        </div>
      )}
      <SearchEntities
        label="Nadawca"
        doNotRemove
        removeFocus
        onChoose={(e: IEntity) => setSender(e)}
      />
      {renderTypeSelect(0)}
      <Input
        variant="outlined"
        label="Tytuł"
        value={attMeta[0]?.title ?? ""}
        placeholder="Tytuł"
        onChange={(v: string) => updateMeta(0, { title: v })}
      />
      <Input
        variant="outlined"
        label="Znak pisma / nr faktury / nr umowy"
        value={attMeta[0]?.signature ?? ""}
        placeholder="Znak pisma"
        onChange={(v: string) => updateMeta(0, { signature: v })}
      />
      {mainIsContract && (
        <div className={styles.cruRow}>
          <button
            type="button"
            className={styles.cruBtn}
            onClick={() => setShowCruMetric(true)}
          >
            <FontAwesomeIcon icon={faFileContract} />
            {cruMetric
              ? "Metryka CRU umowy — edytuj"
              : "Metryka CRU umowy — wypełnij"}
          </button>
          <span className={styles.cruHint}>
            Umowa trafi do rejestru umów; nadawca zostanie pierwszą stroną,
            metrykę można uzupełnić później.
          </span>
        </div>
      )}
      <div className={styles.twoCol}>
        <FormControl fullWidth size="small">
          <InputLabel>Forma doręczenia</InputLabel>
          <Select
            label="Forma doręczenia"
            value={deliveryKindId}
            onChange={(e) => {
              const v = e.target.value as string | number;
              setDeliveryKindId(v === "" ? "" : Number(v));
            }}
          >
            {(init?.DeliveryKinds ?? []).map((k) => (
              <MenuItem key={k.Id} value={k.Id}>
                {k.Name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <DatePickerNew
          showTime={false}
          onlyStart
          startOf={receiveDate}
          setDate={(_type: unknown, date: Date) => setReceiveDate(date)}
        />
      </div>
      <Input
        variant="outlined"
        label="Identyfikator przesyłki (nr nadania)"
        value={carrierSignature}
        placeholder="Identyfikator przesyłki"
        onChange={setCarrierSignature}
      />
      <div className={styles.twoCol}>
        <label className={styles.check}>
          <Checkbox
            checked={attMeta[0]?.isDigital ?? false}
            onChange={(e) =>
              updateMeta(0, {
                isDigital: e.target.checked,
                digitalMapping: e.target.checked || attMeta[0]?.digitalMapping,
              })
            }
          />
          Forma elektroniczna
        </label>
        <label className={styles.check}>
          <Checkbox
            checked={attMeta[0]?.digitalMapping ?? false}
            onChange={(e) =>
              updateMeta(0, { digitalMapping: e.target.checked })
            }
          />
          Odwzorowanie cyfrowe
        </label>
      </div>
    </div>
  );

  const renderDescription = () => (
    <div className={styles.row}>
      <div className={styles.stepHeader}>Opis przesyłki</div>
      <div className={styles.readonly}>
        Kategoria (z głównego załącznika): {mainTypeName}
      </div>
      <div className={styles.twoCol}>
        <FormControl fullWidth size="small">
          <InputLabel>Biuro podawcze</InputLabel>
          <Select
            label="Biuro podawcze"
            value={registryOfficeId}
            onChange={(e) => setRegistryOfficeId(e.target.value as string)}
          >
            <MenuItem value="">—</MenuItem>
            {(init?.RegistryOffices ?? []).map((o) => (
              <MenuItem key={o.Id} value={o.Id}>
                {o.Name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>Skład</InputLabel>
          {/* W edycji skład jest tylko do odczytu — zmiana rozjechałaby numerację (sygnaturę). */}
          <Select
            label="Skład"
            value={parcelStoreId}
            onChange={(e) => setParcelStoreId(e.target.value as string)}
            disabled={isEdit}
          >
            <MenuItem value="">—</MenuItem>
            {(init?.ParcelStores ?? []).map((s) => (
              <MenuItem key={s.Id} value={s.Id}>
                {s.Name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className={styles.twoCol}>
        <FormControl fullWidth size="small">
          <InputLabel>Dekretujący (odbiorca)</InputLabel>
          {/* Biuro podawcze przekazuje zawsze do jednej osoby — domyślnego dekretującego
              z Ustawień → Biuro podawcze; dalszy obieg idzie dekretami między pracownikami.
              Gdy domyślny nie jest ustawiony, rejestrujący wybiera odbiorcę sam. */}
          <Select
            label="Dekretujący (odbiorca)"
            value={receiverId}
            onChange={(e) => setReceiverId(e.target.value as string)}
            disabled={isEdit || !!init?.DefaultReceiverId}
          >
            {isEdit &&
              receiverId &&
              !(init?.Receivers ?? []).some((r) => r.Id === receiverId) && (
                <MenuItem value={receiverId}>
                  {edit?.ReceiverName ?? "—"}
                </MenuItem>
              )}
            {(init?.Receivers ?? []).map((r) => (
              <MenuItem key={r.Id} value={r.Id}>
                {r.Label}
                {r.IsMe ? " (ja)" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>Dostęp</InputLabel>
          <Select
            label="Dostęp"
            value={access}
            onChange={(e) => setAccess(Number(e.target.value) as EParcelAccess)}
          >
            <MenuItem value={EParcelAccess.Public}>Publiczny</MenuItem>
            <MenuItem value={EParcelAccess.Protected}>
              Publiczny częściowo
            </MenuItem>
            <MenuItem value={EParcelAccess.Private}>Niepubliczny</MenuItem>
          </Select>
        </FormControl>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case WizardStep.Attachments:
        return renderAttachments();
      case WizardStep.ParcelState:
        return renderParcelState();
      case WizardStep.MainAttachment:
        return renderMainAttachment();
      case WizardStep.Description:
        return renderDescription();
    }
  };

  return (
    <ModalLayout
      name={isEdit ? "editIncomingParcel" : "createPostage"}
      dock={DockPosition.DISABLED}
      off
      customX={8}
      customY={0}
      ignore
      refWrapper={refWrapper}
      isAnimation
      fullHeight
    >
      <div className={styles.wrapper}>
        <div className={styles.title}>
          {isEdit
            ? `Edycja przesyłki${
                edit?.ParcelNumber ? ` ${edit.ParcelNumber}` : ""
              }`
            : "Rejestracja przesyłki przychodzącej"}
          <CloseIcon className={styles.closeIcon} onClick={close} />
        </div>

        <div className={styles.content}>
          {isEdit && !editLoaded ? (
            <div className={styles.hint}>Ładowanie danych przesyłki…</div>
          ) : (
            renderStep()
          )}
          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.footer}>
          {stepIndex > 0 && !isLoading && (
            <HollowButton text="Wstecz" onClick={goBack} />
          )}
          <div className={styles.footerRight}>
            {!isLast && (
              <Button text="Dalej" onClick={goNext} disabled={isLoading} />
            )}
            {isLast && isEdit && (
              <Button
                text="Zapisz zmiany"
                onClick={() => submit(false)}
                loading={isLoading}
              />
            )}
            {isLast && !isEdit && (
              <>
                <Button
                  text="Zapisz"
                  onClick={() => submit(false)}
                  loading={isLoading}
                />
                <Button
                  text="Przekaż"
                  onClick={() => submit(true)}
                  loading={isLoading}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {blob && <ShowPreviewModal toggle={removeBlob} blob={blob} />}

      {showScan && (
        <ScanDocumentModal
          handleClose={() => setShowScan(false)}
          onComplete={(pdf) => addNewFile(pdf)}
        />
      )}
      {showCruMetric && (
        <ContractCruMetricModal
          title="Metryka umowy (CRU)"
          subtitle={attMeta[0]?.title || undefined}
          initial={
            cruMetric ?? {
              ...emptyCruMetric(attMeta[0]?.title ?? ""),
              AgreementNumber: attMeta[0]?.signature || null,
              AgreementDate: receiveDate
                ? moment(receiveDate).format("YYYY-MM-DD")
                : null,
              Sides: sender
                ? [
                    {
                      Country: "PL",
                      SideType: sender.LastName ? "SU02" : "SU01",
                      IsConsortium: false,
                      Name: sender.Name ?? sender.Label ?? null,
                      FirstName: sender.FirstName ?? null,
                      LastName: sender.LastName ?? null,
                      SourceEntityId: null,
                    },
                  ]
                : [],
            }
          }
          deferValidation
          saveLabel="Zapisz metrykę"
          onSave={(m) => {
            setCruMetric(m);
            setShowCruMetric(false);
          }}
          onClose={() => setShowCruMetric(false)}
        />
      )}
    </ModalLayout>
  );
};

export default IncomingParcelWizard;
